import { CaptionsIcon } from "@minervaproject/fds";
import React, { useContext, useEffect, useState } from "react";

import { TD, TH } from "./table";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppSelector } from "hooks/redux_hooks";
import { AvServiceType } from "shared/types";
import {
  buildDatadogFacetedUrl,
  getClassDurationInMinutes,
  getClassStatus,
  getRecordingState,
} from "shared/utils/monitoring";
import { classroomSelectors } from "store";
import { selectLiveCaptionResource } from "store/selectors/classroom";
import { formatDateTime } from "utils/date";
import { getClassDetailURL, getClassRecordingURL, getObserverClassURL } from "utils/monitoring";
import { longDisplayName } from "utils/user";

function ClassInfoHeader() {
  return (
    <thead>
      <tr>
        <TH>ID</TH>
        <TH>Org</TH>
        <TH>Course</TH>
        <TH>Class Start</TH>
        <TH>Class Duration</TH>
        <TH>Status</TH>
        <TH>Teacher</TH>
        <TH>In Breakout</TH>
        <TH>Modality</TH>
        <TH>Recording</TH>
        <TH>Pubsub Server</TH>
        <TH>Monitoring Server</TH>
        <TH>AV Service</TH>
        <TH>Licode Server</TH>
        <TH>Links</TH>
      </tr>
    </thead>
  );
}

function ClassInfoBody() {
  const forumAPI = useContext(ForumAPIContext);
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const avService = useAppSelector(classroomSelectors.selectAvService());
  const licodeServer = useAppSelector(classroomSelectors.selectLicodeServer());
  const currentlyPresentUserCount = useAppSelector(
    classroomSelectors.selectCurrentlyPresentUserCount(),
  );
  const classStatus = getClassStatus(classInfo, currentlyPresentUserCount);
  const teacher = useAppSelector(classroomSelectors.selectTeacher());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const recordingSessionId = useAppSelector(classroomSelectors.selectRecordingSessionId());
  const recordingState = useAppSelector(classroomSelectors.selectRecordingState());
  const { title, state, danger } = getRecordingState(classInfo, recordingSessionId, recordingState);

  const captionsDoc = useAppSelector(selectLiveCaptionResource());
  const [captionsDocUrl, setCaptionsDocUrl] = useState("");

  useEffect(() => {
    if (!captionsDoc) return;
    const fetchCaptionsDocUrl = async () => {
      const url = await forumAPI.getForumDocSignedUrl(captionsDoc.id);
      setCaptionsDocUrl(url);
    };
    fetchCaptionsDocUrl();
  }, [forumAPI, captionsDoc]);

  return (
    <tbody>
      <tr>
        <TD dataTestId="id">{classInfo.id}</TD>
        <TD title={classInfo.org.name} dataTestId="org">
          {classInfo.org.short_name}
        </TD>
        <TD dataTestId="course">{classInfo.course.course_code}</TD>
        <TD dataTestId="date" noWrap>
          {formatDateTime(classInfo.date)}
        </TD>
        <TD dataTestId="duration">{`${getClassDurationInMinutes(classInfo)}`}</TD>
        <TD
          dataTestId="status"
          conditionForInfo={["Active", "In Progress", "Starting soon"].includes(classStatus)}
        >
          {classStatus}
        </TD>
        <TD dataTestId="teacher">{longDisplayName(teacher)}</TD>
        <TD dataTestId="in-breakout">{activeBreakoutSessionId ? "Yes" : "No"}</TD>
        <TD dataTestId="modality">{classInfo.type}</TD>
        <TD dataTestId="recording" title={title} conditionForDanger={danger}>
          {state}
        </TD>
        <TD dataTestId="flight-server">{`${classInfo.flight_server} (${classInfo.section.flight_group})`}</TD>
        <TD conditionForDanger={!classInfo.flight_monitoring_server}>
          {classInfo.flight_monitoring_server
            ? `${classInfo.flight_monitoring_server} (${classInfo.section.flight_group})`
            : "None"}
        </TD>
        <TD dataTestId="av-service">{avService || ""}</TD>
        <TD dataTestId="licode-server">
          {avService === AvServiceType.Licode ? licodeServer || "" : "N/A"}
        </TD>
        <TD noWrap>
          <a
            className="px1 large"
            target="_blank"
            rel="noreferrer"
            href={getObserverClassURL(classInfo)}
          >
            👀
          </a>
          <a
            className="px1 large"
            target="_blank"
            rel="noreferrer"
            href={getClassDetailURL(classInfo)}
          >
            ✏️
          </a>
          <a
            className="px1 large"
            target="_blank"
            rel="noreferrer"
            href={getClassRecordingURL(classInfo)}
          >
            🎥
          </a>
          <a
            className="px1 pt1 large"
            href={buildDatadogFacetedUrl({ classId: classInfo.id })}
            target="_blank"
            rel="noreferrer"
          >
            🐕
          </a>
          {captionsDocUrl ? (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <a href={captionsDocUrl} target="_blank" rel="noreferrer">
              <span>
                <CaptionsIcon height={16} style={{ marginTop: "12px", borderRadius: "0px" }} />
              </span>
            </a>
          ) : (
            ""
          )}
        </TD>
      </tr>
    </tbody>
  );
}

function ClassInfo() {
  return (
    <table
      className="fds-table table-hover"
      style={{ tableLayout: "auto" }}
      data-testid="class-info-table"
    >
      <ClassInfoHeader />
      <ClassInfoBody />
    </table>
  );
}

export { ClassInfoHeader, ClassInfoBody, ClassInfo };
